import React, { useState, useMemo } from 'react';
import { ZoomIn, ZoomOut, Maximize2, ChevronDown, ChevronUp, Search, Filter, MapPin, Mail, Phone, Users } from 'lucide-react';

const colors = {
  primary: '#2E8B98',
  success: '#62BB46',
  warning: '#F26522',
  danger: '#DC3545',
  background: '#F8F9FA',
  text: '#212529',
  lightText: '#6C757D',
  shadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
  insetShadow: 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)',
};

const DEPARTMENT_COLORS = {
  Cardiology: '#2E8B98', Dermatology: '#256F7A', Emergency: '#DC3545',
  'Family Medicine': '#62BB46', 'Internal Medicine': '#4FA038', Neurology: '#6F42C1',
  Oncology: '#F26522', Orthopedics: '#20C997', Pediatrics: '#2E8B98',
  Psychiatry: '#8B5CF6', Radiology: '#256F7A', Surgery: '#DC3545',
  Laboratory: '#F26522', Pharmacy: '#62BB46', Administration: '#6C757D',
  'Information Technology': '#2E8B98', 'Human Resources': '#F26522', Finance: '#256F7A',
  Engineering: '#2E8B98', Sales: '#62BB46', HR: '#F26522',
  Marketing: '#DC3545', Operations: '#20C997',
  default: '#6C757D',
};

const STATUS_COLORS = {
  active: colors.success,
  inactive: colors.lightText,
  onLeave: colors.warning,
  on_leave: colors.warning,
  terminated: colors.danger,
};

function getStatusColor(status = 'active') {
  return STATUS_COLORS[status] || STATUS_COLORS.active;
}

function getDepartmentColor(department) {
  return DEPARTMENT_COLORS[department] || DEPARTMENT_COLORS.default;
}

function buildTree(personnel) {
  const map = new Map();
  const roots = [];

  personnel.forEach(emp => {
    map.set(emp.id, {
      ...emp,
      // Normalize field names for tree rendering
      name: emp.name || `${emp.firstName || emp.first_name || ''} ${emp.lastName || emp.last_name || ''}`.trim(),
      title: emp.jobTitle || emp.job_title || '',
      department: emp.department || '',
      email: emp.email || '',
      phone: emp.phone || '',
      status: emp.status || 'active',
      children: [],
    });
  });

  personnel.forEach(emp => {
    if (!emp.supervisor || emp.supervisor === '' || emp.supervisor === emp.id) {
      roots.push(map.get(emp.id));
    } else {
      const parent = map.get(emp.supervisor);
      if (parent) {
        parent.children.push(map.get(emp.id));
      } else {
        roots.push(map.get(emp.id));
      }
    }
  });

  return roots;
}

function TreeNode({ node, expanded, onToggle, onNodeClick, onHover, hoveredNodeId, searchHighlight, departmentFilter, level = 0 }) {
  const avatarBg = getDepartmentColor(node.department);
  const statusColor = getStatusColor(node.status);

  const isHighlighted = searchHighlight && node.name.toLowerCase().includes(searchHighlight.toLowerCase());
  const isDepartmentMatch = !departmentFilter || node.department === departmentFilter;
  const isVisible = !searchHighlight || isHighlighted || node.children.some(child => isDepartmentMatch);

  if (!isVisible) return null;

  const nodeStyle = {
    padding: '16px',
    borderRadius: '10px',
    background: colors.background,
    boxShadow: colors.shadow,
    marginBottom: '12px',
    minWidth: '280px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    transform: hoveredNodeId === node.id ? 'translateY(-2px)' : 'translateY(0)',
    opacity: isHighlighted ? 1 : !searchHighlight ? 1 : 0.4,
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
  };

  const avatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: avatarBg,
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    flexShrink: 0,
    position: 'relative',
  };

  const statusDotStyle = {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: statusColor,
    position: 'absolute',
    bottom: '0',
    right: '0',
    border: '2px solid #FFFFFF',
  };

  const infoStyle = {
    flex: 1,
  };

  const nameStyle = {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '700',
    color: colors.text,
  };

  const titleStyle = {
    margin: '0',
    fontSize: '12px',
    color: colors.lightText,
    fontWeight: '500',
  };

  const controlsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const toggleButtonStyle = {
    padding: '4px 8px',
    borderRadius: '6px',
    border: 'none',
    background: '#FFFFFF',
    boxShadow: colors.shadow,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.primary,
    transition: 'all 0.2s',
  };

  const detailsStyle = {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid #E9ECEF`,
    fontSize: '11px',
    color: colors.lightText,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const childrenContainerStyle = {
    marginLeft: '24px',
    marginTop: '12px',
    paddingLeft: '24px',
    position: 'relative',
    borderLeft: `2px solid #E9ECEF`,
  };

  const children = node.children || [];
  const hasChildren = children.length > 0;

  return (
    <div
      key={node.id}
      style={{ marginBottom: '0' }}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
    >
      <div style={nodeStyle} onClick={() => onNodeClick(node)}>
        <div style={headerStyle}>
          <div style={{ display: 'flex', gap: '12px', flex: 1 }}>
            <div style={avatarStyle}>
              {node.name.split(' ').map(n => n[0]).join('')}
              <div style={statusDotStyle} />
            </div>
            <div style={infoStyle}>
              <p style={nameStyle}>{node.name}</p>
              <p style={titleStyle}>{node.title}</p>
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={12} color={colors.lightText} />
                <span>{node.department}</span>
              </div>
            </div>
          </div>
          {hasChildren && (
            <div style={controlsStyle}>
              <button
                style={toggleButtonStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(node.id);
                }}
              >
                {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>
          )}
        </div>

        {hoveredNodeId === node.id && (
          <div style={detailsStyle}>
            {node.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Mail size={12} />
                {node.email}
              </div>
            )}
            {node.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Phone size={12} />
                {node.phone}
              </div>
            )}
            {hasChildren && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Users size={12} />
                {children.length} direct report{children.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
      </div>

      {hasChildren && expanded && (
        <div style={childrenContainerStyle}>
          {children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              expanded={expanded}
              onToggle={onToggle}
              onNodeClick={onNodeClick}
              onHover={onHover}
              hoveredNodeId={hoveredNodeId}
              searchHighlight={searchHighlight}
              departmentFilter={departmentFilter}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgTree({ roots, expandedNodes, onToggleNode, onNodeClick, hoveredNodeId, onHover, searchHighlight, departmentFilter }) {
  return (
    <div style={{ paddingLeft: '0' }}>
      {roots.map(root => (
        <TreeNode
          key={root.id}
          node={root}
          expanded={expandedNodes.has(root.id)}
          onToggle={onToggleNode}
          onNodeClick={onNodeClick}
          onHover={onHover}
          hoveredNodeId={hoveredNodeId}
          searchHighlight={searchHighlight}
          departmentFilter={departmentFilter}
          level={0}
        />
      ))}
    </div>
  );
}

function NodeDetailPopup({ node, onClose }) {
  if (!node) return null;

  const popupStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
    background: colors.background,
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
    padding: '24px',
    minWidth: '360px',
    maxWidth: '480px',
  };

  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 999,
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
  };

  const avatarStyle = {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: getDepartmentColor(node.department),
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
    position: 'relative',
  };

  const statusDot = {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: getStatusColor(node.status),
    position: 'absolute',
    bottom: '0',
    right: '0',
    border: '2px solid #FFFFFF',
  };

  const infoStyle = {
    flex: 1,
  };

  const nameStyle = {
    margin: '0 0 4px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: colors.text,
  };

  const titleStyle = {
    margin: '0',
    fontSize: '14px',
    color: colors.lightText,
  };

  const detailsStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
    padding: '16px',
    background: '#FFFFFF',
    borderRadius: '8px',
    boxShadow: colors.shadow,
  };

  const detailRowStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  };

  const labelStyle = {
    fontSize: '12px',
    fontWeight: '600',
    color: colors.lightText,
    textTransform: 'uppercase',
    minWidth: '60px',
  };

  const valueStyle = {
    fontSize: '14px',
    color: colors.text,
    flex: 1,
  };

  const buttonStyle = {
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    background: colors.primary,
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s',
    width: '100%',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '12px',
    right: '12px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: colors.text,
  };

  const childrenCount = node.children ? node.children.length : 0;

  return (
    <>
      <div style={backdropStyle} onClick={onClose} />
      <div style={popupStyle}>
        <button style={closeButtonStyle} onClick={onClose}>×</button>

        <div style={headerStyle}>
          <div style={avatarStyle}>
            {node.name.split(' ').map(n => n[0]).join('')}
            <div style={statusDot} />
          </div>
          <div style={infoStyle}>
            <p style={nameStyle}>{node.name}</p>
            <p style={titleStyle}>{node.title}</p>
          </div>
        </div>

        <div style={detailsStyle}>
          <div style={detailRowStyle}>
            <div style={labelStyle}>
              <MapPin size={14} color={colors.lightText} style={{ marginTop: '2px' }} />
            </div>
            <div style={valueStyle}>
              {node.department}
            </div>
          </div>

          {node.email && (
            <div style={detailRowStyle}>
              <div style={labelStyle}>
                <Mail size={14} color={colors.lightText} style={{ marginTop: '2px' }} />
              </div>
              <div style={valueStyle}>
                {node.email}
              </div>
            </div>
          )}

          {node.phone && (
            <div style={detailRowStyle}>
              <div style={labelStyle}>
                <Phone size={14} color={colors.lightText} style={{ marginTop: '2px' }} />
              </div>
              <div style={valueStyle}>
                {node.phone}
              </div>
            </div>
          )}

          {childrenCount > 0 && (
            <div style={detailRowStyle}>
              <div style={labelStyle}>
                <Users size={14} color={colors.lightText} style={{ marginTop: '2px' }} />
              </div>
              <div style={valueStyle}>
                {childrenCount} direct report{childrenCount !== 1 ? 's' : ''}
              </div>
            </div>
          )}

          {node.status && (
            <div style={detailRowStyle}>
              <div style={labelStyle}>Status</div>
              <div style={{
                ...valueStyle,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: getStatusColor(node.status) + '20',
                color: getStatusColor(node.status),
                fontWeight: '600',
                fontSize: '12px',
              }}>
                {node.status.charAt(0).toUpperCase() + node.status.slice(1)}
              </div>
            </div>
          )}
        </div>

        <button style={buttonStyle} onClick={onClose}>
          View Profile
        </button>
      </div>
    </>
  );
}

export default function OrgChartTab({ personnel }) {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  const roots = useMemo(() => buildTree(personnel), [personnel]);

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    const allNodeIds = new Set();
    const addNodeIds = (nodes) => {
      nodes.forEach(node => {
        allNodeIds.add(node.id);
        if (node.children) addNodeIds(node.children);
      });
    };
    addNodeIds(roots);
    setExpandedNodes(allNodeIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  const departments = useMemo(() => {
    const depts = new Set();
    const collectDepts = (nodes) => {
      nodes.forEach(node => {
        if (node.department) depts.add(node.department);
        if (node.children) collectDepts(node.children);
      });
    };
    collectDepts(roots);
    return Array.from(depts).sort();
  }, [roots]);

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '0',
  };

  const controlsStyle = {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
    flexWrap: 'wrap',
  };

  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#FFFFFF',
    boxShadow: colors.shadow,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: colors.text,
    fontSize: '13px',
    fontWeight: '600',
    transition: 'all 0.2s',
  };

  const inputStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#FFFFFF',
    boxShadow: colors.shadow,
    color: colors.text,
    fontSize: '13px',
    flex: 1,
    minWidth: '200px',
  };

  const selectStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#FFFFFF',
    boxShadow: colors.shadow,
    color: colors.text,
    fontSize: '13px',
    cursor: 'pointer',
  };

  const treeContainerStyle = {
    padding: '20px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
    overflow: 'auto',
    maxHeight: 'calc(100vh - 300px)',
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    transition: 'transform 0.2s',
  };

  const emptyStateStyle = {
    padding: '40px 20px',
    textAlign: 'center',
    color: colors.lightText,
    fontSize: '14px',
  };

  if (!personnel || personnel.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>
          No personnel data available to display organizational chart.
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={controlsStyle}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={buttonStyle}
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
          >
            <ZoomOut size={16} />
            Zoom Out
          </button>
          <button
            style={buttonStyle}
            onClick={() => setZoom(Math.min(2, zoom + 0.1))}
          >
            <ZoomIn size={16} />
            Zoom In
          </button>
          <button
            style={buttonStyle}
            onClick={() => setZoom(1)}
          >
            <Maximize2 size={16} />
            Reset
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={14} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.lightText,
              pointerEvents: 'none',
            }} />
            <input
              type="text"
              placeholder="Search personnel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                ...inputStyle,
                paddingLeft: '32px',
              }}
            />
          </div>

          <div style={{ position: 'relative' }}>
            <Filter size={14} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.lightText,
              pointerEvents: 'none',
            }} />
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              style={{
                ...selectStyle,
                paddingLeft: '32px',
              }}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={buttonStyle}
            onClick={expandAll}
          >
            Expand All
          </button>
          <button
            style={buttonStyle}
            onClick={collapseAll}
          >
            Collapse All
          </button>
        </div>
      </div>

      <div style={treeContainerStyle}>
        {roots.length === 0 ? (
          <div style={emptyStateStyle}>
            No organizational hierarchy found. Add personnel with supervisor assignments.
          </div>
        ) : (
          <OrgTree
            roots={roots}
            expandedNodes={expandedNodes}
            onToggleNode={toggleNode}
            onNodeClick={setSelectedNode}
            hoveredNodeId={hoveredNodeId}
            onHover={setHoveredNodeId}
            searchHighlight={searchTerm}
            departmentFilter={departmentFilter}
          />
        )}
      </div>

      {selectedNode && (
        <NodeDetailPopup
          node={selectedNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
}
